import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, authHeaders, formatApiErrorDetail } from '@/lib/api';
import { FIELD_CLS } from './adminShared';

export const CONTENT_GROUPS = [
  ['Homepage', [
    ['home_roster_overline', 'Roster overline (small label)', 'text'],
    ['home_roster_title', 'Roster heading', 'text'],
    ['home_store_overline', 'Store band overline', 'text'],
    ['home_store_title', 'Store band heading', 'text'],
    ['home_store_btn', 'Store band button label', 'text'],
    ['hero_hint', 'Hero click hint', 'text'],
  ]],
  ['Artists Page', [
    ['artists_overline', 'Page overline', 'text'],
    ['artists_title', 'Page heading', 'text'],
  ]],
  ['Videos Page', [
    ['videos_overline', 'Page overline', 'text'],
    ['videos_title', 'Page heading', 'text'],
  ]],
  ['Mailing List Page', [
    ['mailing_title', 'Page heading', 'text'],
  ]],
  ['Shop Page', [
    ['shop_overline', 'Page overline', 'text'],
    ['shop_title', 'Page heading', 'text'],
    ['shop_btn', 'Button label', 'text'],
    ['shop_soon', 'Fallback text when no store link is set', 'text'],
  ]],
  ['Contact Page', [
    ['contact_overline', 'Page overline', 'text'],
    ['contact_title', 'Page heading', 'text'],
  ]],
  ['Footer', [
    ['footer_tagline', 'Tagline under logo', 'textarea'],
    ['footer_news_copy', 'Newsletter blurb', 'text'],
    ['footer_note', 'Newsletter fine print', 'text'],
    ['footer_copyright', 'Copyright line', 'text'],
  ]],
];

export default function AdminContentPanel({ token }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get('/content').then(({ data }) => setForm(data || {})).catch(() => setForm({}));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put('/admin/content', form, { headers: authHeaders(token) });
      setForm(data);
      toast.success('Site content saved');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!form) return <p className="meta-label text-brayo-dim">Loading…</p>;

  return (
    <div data-testid="admin-content">
      <h2 className="display-title mb-2 text-2xl md:text-3xl">Site Content</h2>
      <p className="mb-8 text-sm text-brayo-mute">
        Every heading, label and line of copy on the website. Leave a field empty to use the default.
      </p>
      <form data-testid="admin-content-form" onSubmit={submit} className="space-y-10">
        {CONTENT_GROUPS.map(([group, fields]) => (
          <fieldset key={group} className="border border-brayo-line bg-white p-6">
            <legend className="meta-label px-2 text-brayo-blue">{group}</legend>
            <div className="space-y-4">
              {fields.map(([key, label, type]) => (
                <label key={key} className="block">
                  <span className="meta-label mb-2 block text-brayo-dim">{label}</span>
                  {type === 'textarea' ? (
                    <textarea
                      data-testid={`admin-content-${key}`}
                      rows={2}
                      value={form[key] ?? ''}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      className={`${FIELD_CLS} resize-none`}
                    />
                  ) : (
                    <input
                      data-testid={`admin-content-${key}`}
                      value={form[key] ?? ''}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      className={FIELD_CLS}
                    />
                  )}
                </label>
              ))}
            </div>
          </fieldset>
        ))}
        <button data-testid="admin-content-save-btn" type="submit" className="meta-label bg-brayo-blue px-8 py-3.5 text-white transition-colors hover:bg-brayo-charcoal">
          Save Site Content
        </button>
      </form>
    </div>
  );
}